import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav";
import { auth, db } from "../config";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import SeedPlans from "../Components/SeedPlans";
import Footer from "../Components/Footer";

function Invest() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [amounts, setAmounts] = useState({});
  const [messages, setMessages] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setBalance(userDoc.data().balance || 0);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setMessages({ global: "Error fetching user data." });
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    const fetchPlans = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "investmentPlans"));
        const fetchedPlans = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPlans(fetchedPlans);
      } catch (error) {
        console.error("Error fetching plans:", error);
        setMessages({ global: "Error fetching investment plans." });
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
    return () => unsubscribe();
  }, [navigate]);

  const handleInvest = async (e, plan) => {
    e.preventDefault();
    setSubmitting((prev) => ({ ...prev, [plan.id]: true }));

    const investmentAmount = parseFloat(amounts[plan.id] || 0);

    if (isNaN(investmentAmount) || investmentAmount <= 0) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "Please enter a valid amount.",
      }));
      setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
      return;
    }

    if (investmentAmount < plan.min) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: `Amount must be at least $${plan.min}.`,
      }));
      setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
      return;
    }

    if (plan.max !== "Unlimited" && investmentAmount > plan.max) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: `Amount cannot exceed $${plan.max}.`,
      }));
      setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
      return;
    }

    if (investmentAmount > balance) {
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "Insufficient balance.",
      }));
      setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
      return;
    }

    try {
      const investmentsRef = collection(db, "investments");
      const activeQuery = query(
        investmentsRef,
        where("userId", "==", user.uid),
        where("plan", "==", plan.name),
        where("status", "==", "active")
      );
      const activeSnapshot = await getDocs(activeQuery);

      if (!activeSnapshot.empty) {
        setMessages((prev) => ({
          ...prev,
          [plan.id]: `You already have an active investment in the "${plan.name}" plan.`,
        }));
        setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
        return;
      }

      await addDoc(collection(db, "investments"), {
        userId: user.uid,
        email: user.email,
        plan: plan.name,
        roi: plan.roi,
        duration: plan.duration,
        amount: investmentAmount,
        createdAt: new Date(),
        status: "active",
      });

      const userDocRef = doc(db, "users", user.uid);
      const newBalance = balance - investmentAmount;
      await updateDoc(userDocRef, { balance: newBalance });

      setBalance(newBalance);
      setAmounts((prev) => ({ ...prev, [plan.id]: "" }));
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "Investment activated successfully!",
      }));
    } catch (error) {
      console.error("Error activating investment:", error);
      setMessages((prev) => ({
        ...prev,
        [plan.id]: "Failed to activate investment: " + error.message,
      }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [plan.id]: false }));
    }
  };


  return (
    <div>
      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .footer-block {
            min-height: 400px;
            padding: 50px 0;
            background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg);
          }
          .foot-header {
            font-family: "Montserrat", Sans-serif;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 25px;
            color: #4A4A4A;
          }
          .foot {
            display: block;
            font-size: small;
            margin-bottom: 8px;
            font-family: "Montserrat", Sans-serif;
            color: #A1A39E !important;
          }
          .foot:hover {
            text-decoration: none !important;
            color: #FE413C !important;
          }
          .foot::before {
            content: "-";
            margin-right: 3px;
            color: #FE413C;
          }
          .footer-credit {
            font-family: "Montserrat", Sans-serif;
            color: #FE413C;
            font-size: small;
          }
          .footer-block .small-image {
            width: 250px;
            height: auto;
          }
          .pricing-table {
            border-radius: 8px;
            transition: transform 0.2s;
          }
          .pricing-table:hover {
            transform: translateY(-5px);
          }
          .pricing-features .feature {
            margin-bottom: 10px;
            font-size: 1rem;
          }
          .pricing-features .feature span {
            float: right;
            font-weight: bold;
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="mt-2 mb-4">
                <h2 className="title1 text-dark">Investment Plans</h2>
                <p className="text-dark">Available Balance: ${balance.toFixed(2)}</p>
                {messages.global && (
                  <div className={`message ${messages.global.includes("success") ? "success" : "error"}`}>
                    {messages.global}
                  </div>
                )}
              </div>



                {/* <SeedPlans/> */}




              <div className="row">
                {plans.length === 0 ? (
                  <div className="col-12">
                    <p className="text-dark">No investment plans available.</p>
                  </div>
                ) : (
                  plans.map((plan) => (
<div className="col-lg-4 mb-4" key={plan.id}>
  <div className="pricing-table card border bg-light shadow p-4">
    <h2 className="text-dark text-center">{plan.name}</h2>
    <div className="pricing-features">
      <div className="feature text-dark">
        Minimum: <span style={{ color: "white" }}>${plan.min}</span>
      </div>
      <div className="feature text-dark">
        Maximum: <span style={{ color: "white" }}>{plan.max === "Unlimited" ? "Unlimited" : `$${plan.max}`}</span>
      </div>
      <div className="feature text-dark">
        Cumulative Profit: <span style={{ color: "white" }}>{plan.roi}%</span>
      </div>
      <div className="feature text-dark">
        Referral Bonus: <span style={{ color: "white" }}>{plan.referralBonus || 5}%</span>
      </div>
      <div className="feature text-dark">
        Duration: <span style={{ color: "white" }}>{plan.duration}</span>
      </div>
    </div>
    <form onSubmit={(e) => handleInvest(e, plan)}>
      <h5 className="text-dark mt-3">
        Amount to invest: (${plan.min} default)
      </h5>
      <input
        type="number"
        min={plan.min}
        max={plan.max !== "Unlimited" ? plan.max : undefined}
        placeholder={`$${plan.min}`}
        className="form-control text-dark bg-light"
        value={amounts[plan.id] || ""}
        onChange={(e) =>
          setAmounts((prev) => ({
            ...prev,
            [plan.id]: e.target.value,
          }))
        }
        required
        step="0.01"
      />
      {messages[plan.id] && (
        <div className={`message ${messages[plan.id].includes("success") ? "success" : "error"}`}>
          {messages[plan.id]}
        </div>
      )}
      <button
        type="submit"
        className="btn btn-block pricing-action btn-primary mt-3"
        disabled={submitting[plan.id]}
      >
        {submitting[plan.id] ? "Processing..." : "Invest Now"}
      </button>
    </form>
  </div>
</div>
                  ))
                )}
              </div>
            </div>
<Footer/>
            <div
              style={{
                position: "fixed",
                left: 0,
                bottom: 0,
                zIndex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  boxSizing: "content-box",
                  textAlign: "right",
                  lineHeight: "14px",
                  fontSize: "12px",
                  fontFeatureSettings: "normal",
                  textSizeAdjust: "100%",
                  padding: "0px",
                  margin: "0px",
                }}
              >
                <div style={{ height: "40px" }}>
                  <iframe
                    src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
                    width="100%"
                    height={36}
                    scrolling="auto"
                    marginWidth={0}
                    marginHeight={0}
                    frameBorder={0}
                    border={0}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Invest;
